import { useCallback, useEffect, useState } from "preact/hooks";
import type { JSX } from "preact";
import { api } from "../../api";
import { useI18n } from "../../lib/i18n";
import { Section, SettingRow } from "./common";

type State = Awaited<ReturnType<typeof api.telegramState>>;

/**
 * Linking the team's Telegram group and this operator's own account. The
 * console only mints a short code; the link is made by typing it in
 * Telegram, the one place that can vouch for which chat and account sent it.
 */
export function TelegramSection({ isAdmin }: { isAdmin: boolean }): JSX.Element | null {
  const { t: all } = useI18n();
  const t = all.telegram;
  const [state, setState] = useState<State | null>(null);
  const [codes, setCodes] = useState<{ me?: string; group?: string }>({});
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await api.telegramState());
    } catch {
      // An older server has no such route; the section simply does not appear.
      setState(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // While a code is waiting to be typed in Telegram, watch for the link to appear.
  useEffect(() => {
    if (!codes.me && !codes.group) return;
    const timer = setInterval(() => void load(), 4000);
    return () => clearInterval(timer);
  }, [codes, load]);

  async function getCode(kind: "me" | "group"): Promise<void> {
    setError(null);
    try {
      const { code } = await api.telegramCode(kind);
      setCodes((c) => ({ ...c, [kind]: code }));
    } catch {
      setError(t.failed);
    }
  }

  async function unlink(kind: "me" | "group"): Promise<void> {
    setError(null);
    try {
      await api.telegramUnlink(kind);
      setCodes((c) => ({ ...c, [kind]: undefined }));
      await load();
    } catch {
      setError(t.failed);
    }
  }

  if (!state) return null;

  function codeLine(kind: "me" | "group"): JSX.Element | null {
    const code = codes[kind];
    if (!code) return null;
    const command = `${kind === "group" ? "/bagla" : "/ben"} ${code}`;
    return (
      <p class="hint">
        {t.codeHint}
        <code style={{ userSelect: "all", fontSize: "1.05em" }}>{command}</code>{" "}
        <button class="btn sm ghost" onClick={() => void navigator.clipboard?.writeText(command)}>
          {t.copy}
        </button>
      </p>
    );
  }

  return (
    <Section
      title={t.title}
      intro={
        <>
          {t.intro}
          {state.botUsername ? (
            <>
              {" "}
              <a href={`https://t.me/${state.botUsername}`} target="_blank" rel="noopener noreferrer">
                {t.bot(state.botUsername)}
              </a>
            </>
          ) : null}
        </>
      }
    >
      {!state.configured ? (
        <p class="hint">{t.notConfigured}</p>
      ) : (
        <>
          <SettingRow
            title={t.group}
            hint={
              <>
                {state.group ? t.groupLinked(state.group.title) : t.groupNone}
                {!state.group ? codeLine("group") : null}
              </>
            }
          >
            {isAdmin ? (
              state.group ? (
                <button class="btn sm" onClick={() => void unlink("group")}>
                  {t.unlink}
                </button>
              ) : (
                <button class="btn sm primary" onClick={() => void getCode("group")}>
                  {t.groupCode}
                </button>
              )
            ) : (
              <span />
            )}
          </SettingRow>
          <SettingRow
            title={t.me}
            hint={
              <>
                {state.me ? t.meLinked(state.me.telegramName) : t.meNone}
                {!state.me ? codeLine("me") : null}
              </>
            }
          >
            {state.me ? (
              <button class="btn sm" onClick={() => void unlink("me")}>
                {t.unlink}
              </button>
            ) : (
              <button class="btn sm primary" onClick={() => void getCode("me")}>
                {t.meCode}
              </button>
            )}
          </SettingRow>
          {error && (
            <div class="alert error" role="status">
              {error}
            </div>
          )}
        </>
      )}
    </Section>
  );
}
